import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { getId as getEventId } from '../../state/event';
import { isFFEnabled } from '../../state/flags';
import { createQueryDefinition, createQueryHook } from '../queryBuilder';

import { keys as keysFn, get as getFn, update as updateFn, oldKeys, oldGet } from './api';

export const GetConfigKeysQuery = createQueryDefinition(
  (isNewConfigUapiEnabled: boolean) => ['config', 'keys', isNewConfigUapiEnabled] as const,
  ({ queryKey: [, , isNewConfigUapiEnabled] }) => (isNewConfigUapiEnabled ? keysFn() : oldKeys()),
);

/*
    1. Example of oldKeys object
    {
      "id": 205,
      "key": "activity_feed_create_post_placeholder",
      "type": "text",
      "default": "Write something... / Text eingeben...",
      "validation": "",
      "precedence": "event",
      "isPurchasable": false,
      "isPurchasableAddon": false,
      "displayName": "Post Placeholder Text for Activity Feed",
      "description": "",
      "price": 0,
      "maxExpiryInDays": null
    }

    2. Example of new keysFn object
    {
      "updatedAt": "2024-06-04T15:46:54.410296+00:00",
      "createdAt": "2024-06-04T15:46:54.410296+00:00",
      "id": "activity_feed_create_post_placeholder",
      "name": "Post Placeholder Text for Activity Feed",
      "parentId": null,
      "description": "",
      "type": "text",
      "maxExpiryInDays": null,
      "default": "Write something... / Text eingeben...",
      "validationSchema": "{'type': 'string', 'default': 'Write something... / Text eingeben...'}"
    }

  // new keysFn returning key name in id is not ideal for the frontend implementation
  // because id should indicate the id of config not the key of it.
  // Hence we are mapping id to key in the select function of useConfigKeysQuery for new API response to make it consistent with old API response and frontend implementation.
  // This will avoid confusion and potential bugs in the future.
*/

export const useConfigKeysQuery = createQueryHook(GetConfigKeysQuery, {
  select: ({ data, meta }) => {
    if (meta?.request?.url === '/cms/v1/configuration_keys') {
      return data;
    }

    return data.map(item => ({ ...item, key: item.id }));
  },
});

export const queryGetConfig = createQueryDefinition(
  (eventId: string | number, isNewConfigUapiEnabled: boolean) =>
    ['event', String(eventId), 'config', isNewConfigUapiEnabled] as const,
  ({ queryKey: [, eventId, , isNewConfigUapiEnabled] }) =>
    isNewConfigUapiEnabled ? getFn(eventId) : oldGet(eventId),
);

/*
    1. Example of oldGet object
    {
        "id": 583522,
        "eventId": 8024,
        "ownerType": "event",
        "ownerId": 0,
        "itemId": 0,
        "keyId": 266,
        "key": "can_people_edit_visibility_preference",
        "value": "0",
        "expiresAt": null
    }

    2. Example of new getFn object
    {
      "value": "1",
      "expiresAt": null,
      "keyId": "is_cms2_event"
    }

    // API is returning key as keyId in the new API. Which is confusing, keys already have their own id field.
    // Hence to avoid confusion we are mapping keyId to key.
*/

export const useConfigQuery = createQueryHook(queryGetConfig, {
  select: ({ data, meta }) => {
    if (meta?.request?.url?.includes('/cms/v1/events/')) {
      return data;
    }

    return data.map(item => ({ ...item, key: item.keyId }));
  },
});

export const useConfigUpdateMutation = () => {
  const queryClient = useQueryClient();
  const isNewConfigUapiEnabled = useSelector(state => isFFEnabled(state, 'new_config_uapi'));
  const eventId = useSelector(getEventId);

  return useMutation({
    mutationFn: ({
      configs,
    }: {
      configs: { key: string; value: string | number; expiresAt?: string | null }[];
    }) =>
      updateFn(
        eventId,
        configs.map(({ key, ...rest }) => ({ ...rest, keyId: key })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryGetConfig.getQueryKey(eventId, isNewConfigUapiEnabled),
      });
    },
  });
};
